import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import Pagination from './Pagination';
import usePageSize from '../../utils/usePageSize';
import RegistrationDetailModal from './RegistrationDetailModal';

import { ta, taf, adminLocale } from '../../i18n/adminT';
const MIN_REFRESH_SECONDS = 5;

function formatDatePart(iso) {
  return new Date(iso).toLocaleDateString(adminLocale());
}

function formatTimePart(iso) {
  return new Date(iso).toLocaleTimeString(adminLocale(), { hour: '2-digit', minute: '2-digit' });
}

const DOC_TYPE_LABELS = {
  carta_identita: "Carta d'identità",
  patente: 'Patente',
  passaporto: 'Passaporto',
};

const emptyFilters = { search: '', riferimento: '', ditta: '', dateFrom: '', dateTo: '' };

// Colonne meno usate quotidianamente: nascoste di default per non dover
// scorrere lo schermo verso destra, riattivabili dal menu "Colonne".
const TOGGLEABLE_COLUMNS = [
  { key: 'riferimento', label: 'Riferimento' },
  { key: 'email', label: 'Email' },
  { key: 'ditta', label: 'Ditta' },
  { key: 'documento', label: 'Documento' },
  { key: 'firma', label: 'Firma' },
  { key: 'privacy', label: 'Privacy' },
  { key: 'sicurezza', label: 'Sicurezza' },
  { key: 'note', label: 'Note' },
  { key: 'dispositivo', label: 'Dispositivo' },
];

function loadStoredColumns() {
  try {
    const raw = localStorage.getItem('exelflow_registrations_columns');
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function loadStoredAutoRefresh() {
  try {
    const raw = localStorage.getItem('exelflow_registrations_autorefresh');
    if (!raw) return { enabled: false, seconds: 30 };
    const parsed = JSON.parse(raw);
    return {
      enabled: Boolean(parsed.enabled),
      seconds: Math.max(MIN_REFRESH_SECONDS, parseInt(parsed.seconds, 10) || 30),
    };
  } catch {
    return { enabled: false, seconds: 30 };
  }
}

export default function RegistrationsTable({ isSuperAdmin, isOperator }) {
  const [filters, setFilters] = useState(emptyFilters);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const { pageSize, pageSizeRef, setPageSize } = usePageSize('registrations');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [closingId, setClosingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [actionError, setActionError] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [noteRegistration, setNoteRegistration] = useState(null);
  const [detailRegistrationId, setDetailRegistrationId] = useState(null);

  const initialAutoRefresh = loadStoredAutoRefresh();
  const [autoRefresh, setAutoRefresh] = useState(initialAutoRefresh.enabled);
  const [refreshSeconds, setRefreshSeconds] = useState(initialAutoRefresh.seconds);
  const [autoMenuOpen, setAutoMenuOpen] = useState(false);
  const autoMenuRef = useRef(null);

  const [visibleColumns, setVisibleColumns] = useState(loadStoredColumns);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const columnsMenuRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('exelflow_registrations_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    if (!columnsMenuOpen) return undefined;
    const handleClickOutside = (e) => {
      if (columnsMenuRef.current && !columnsMenuRef.current.contains(e.target)) setColumnsMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [columnsMenuOpen]);

  const toggleColumn = (key) => {
    setVisibleColumns((v) => {
      const next = { ...v, [key]: !v[key] };
      // La colonna Firma richiede dati non scaricati di default: ricarica
      // subito, sia per popolarla appena attivata sia per liberare memoria
      // quando viene disattivata di nuovo.
      if (key === 'firma') load(filtersRef.current, pageRef.current, { silent: true, includeSignatureOverride: next.firma });
      return next;
    });
  };
  const visibleCount = TOGGLEABLE_COLUMNS.filter((c) => visibleColumns[c.key]).length;

  useEffect(() => {
    if (!autoMenuOpen) return undefined;
    const handleClickOutside = (e) => {
      if (autoMenuRef.current && !autoMenuRef.current.contains(e.target)) setAutoMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [autoMenuOpen]);

  const filtersRef = useRef(filters);
  const pageRef = useRef(page);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { pageRef.current = page; }, [page]);

  const load = async (f = filters, p = page, { silent = false, includeSignatureOverride } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const includeSignature = includeSignatureOverride !== undefined ? includeSignatureOverride : Boolean(visibleColumns.firma);
      const res = await api.getRegistrations(f, p, includeSignature, pageSizeRef.current);
      setRows(res.data);
      setTotal(res.total);
      setPage(p);
      setLastRefreshedAt(new Date());
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(emptyFilters, 1); }, []);

  // Ricorda la preferenza di aggiornamento automatico tra una visita e l'altra
  useEffect(() => {
    localStorage.setItem('exelflow_registrations_autorefresh', JSON.stringify({ enabled: autoRefresh, seconds: refreshSeconds }));
  }, [autoRefresh, refreshSeconds]);

  // Aggiornamento automatico: usa dei ref per leggere sempre filtri/pagina
  // correnti senza dover riavviare il timer ad ogni modifica.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const ms = Math.max(MIN_REFRESH_SECONDS, refreshSeconds) * 1000;
    const id = setInterval(() => {
      load(filtersRef.current, pageRef.current, { silent: true });
    }, ms);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshSeconds]);

  const handleManualRefresh = () => load(filters, page, { silent: true });

  const updateFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    load(filters, 1);
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    load(emptyFilters, 1);
  };

  const handlePageChange = (newPage) => {
    load(filters, newPage);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    load(filters, 1);
  };

  const handleExport = async () => {
    setExportError('');
    setExporting(true);
    try {
      await api.exportRegistrationsCsv(filters);
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleClose = async (row) => {
    if (!window.confirm(
      `Chiudere manualmente la registrazione di "${row.nome} ${row.cognome}" (badge ${row.numero_badge})?\n\n` +
      `Verrà segnata come uscita adesso. Usalo solo se sai per certo che la persona è già uscita senza timbrare.`
    )) return;

    setActionError('');
    setClosingId(row.id);
    try {
      await api.closeRegistration(row.id);
      await load();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setClosingId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(
      `Eliminare definitivamente la registrazione di "${row.nome} ${row.cognome}" (badge ${row.numero_badge})?\n\n` +
      `L'operazione non è reversibile.`
    )) return;

    setActionError('');
    setDeletingId(row.id);
    try {
      await api.deleteRegistration(row.id);
      await load();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 className="admin-page-title">{ta('Registrazioni')}</h1>
          <p className="admin-page-subtitle" style={{ marginBottom: 0 }}>
            {total} {ta('registrazioni totali')}{total > pageSize ? taf(' · pagina {page} di {pages}', { page, pages: Math.max(1, Math.ceil(total / pageSize)) }) : ''}
            {lastRefreshedAt && taf(' · aggiornato alle {time}', { time: lastRefreshedAt.toLocaleTimeString(adminLocale()) })}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div className="toolbar-menu-wrap" ref={columnsMenuRef}>
            <button
              type="button"
              className="btn btn-ghost toolbar-btn"
              onClick={() => setColumnsMenuOpen((v) => !v)}
            >
              {ta('☰ Colonne')}{visibleCount > 0 ? ` (${visibleCount})` : ''}
            </button>
            {columnsMenuOpen && (
              <div className="auto-refresh-popover toolbar-menu-popover">
                <p style={{ padding: '0 12px', fontSize: '0.75rem', color: 'var(--muted-text)', marginBottom: 8 }}>
                  {ta('Colonne aggiuntive da mostrare')}
                </p>
                {TOGGLEABLE_COLUMNS.map((col) => (
                  <label key={col.key} className="checkbox-row" style={{ padding: '0 12px', marginBottom: 8 }}>
                    <input type="checkbox" checked={Boolean(visibleColumns[col.key])} onChange={() => toggleColumn(col.key)} />
                    <span>{ta(col.label)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="toolbar-menu-wrap" ref={autoMenuRef}>
          <button
            type="button"
            className="btn btn-ghost toolbar-btn toolbar-menu-trigger"
            onClick={() => setAutoMenuOpen((v) => !v)}
            aria-label={ta('Altre azioni')}
            title={ta('Altre azioni')}
          >
            ⋯{autoRefresh && <span className="toolbar-menu-dot" />}
          </button>
          {autoMenuOpen && (
            <div className="auto-refresh-popover toolbar-menu-popover">
              <button
                type="button"
                className="toolbar-menu-item"
                onClick={handleManualRefresh}
                disabled={refreshing}
              >
                {refreshing ? ta('Aggiornamento…') : ta('↻ Aggiorna adesso')}
              </button>
              {!isOperator && (
                <button
                  type="button"
                  className="toolbar-menu-item"
                  onClick={handleExport}
                  disabled={exporting}
                >
                  {exporting ? ta('Esportazione…') : ta('⬇ Esporta CSV')}
                </button>
              )}

              <div className="toolbar-menu-divider" />

              <label className="checkbox-row" style={{ marginBottom: autoRefresh ? 10 : 0, padding: '0 12px' }}>
                <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                <span>{ta('Aggiornamento automatico')}</span>
              </label>
              {autoRefresh && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px' }}>
                  <span style={{ fontSize: '0.82rem', color: 'var(--muted-text)' }}>{ta('ogni')}</span>
                  <input
                    type="number"
                    min={MIN_REFRESH_SECONDS}
                    value={refreshSeconds}
                    onChange={(e) => setRefreshSeconds(Math.max(MIN_REFRESH_SECONDS, parseInt(e.target.value, 10) || MIN_REFRESH_SECONDS))}
                    className="search-input"
                    style={{ width: 64, textAlign: 'center' }}
                  />
                  <span style={{ fontSize: '0.82rem', color: 'var(--muted-text)' }}>{ta('secondi')}</span>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {exportError && <div className="form-error">{exportError}</div>}
      {actionError && <div className="form-error">{actionError}</div>}

      <form onSubmit={handleFilterSubmit} className="registrations-filters">
        <input
          className="search-input"
          placeholder={ta('Nome, cognome o badge…')}
          value={filters.search}
          onChange={updateFilter('search')}
          style={{ width: 200 }}
        />
        <input
          className="search-input"
          placeholder={ta('Riferimento…')}
          value={filters.riferimento}
          onChange={updateFilter('riferimento')}
          style={{ width: 160 }}
        />
        <input
          className="search-input"
          placeholder={ta('Ditta…')}
          value={filters.ditta}
          onChange={updateFilter('ditta')}
          style={{ width: 160 }}
        />
        <label className="filter-date-label">
          {ta('Da')}
          <input type="date" className="search-input" value={filters.dateFrom} onChange={updateFilter('dateFrom')} style={{ width: 150 }} />
        </label>
        <label className="filter-date-label">
          {ta('A')}
          <input type="date" className="search-input" value={filters.dateTo} onChange={updateFilter('dateTo')} style={{ width: 150 }} />
        </label>

        <button type="submit" className="btn btn-primary" style={{ width: 'auto', padding: '10px 20px', marginTop: 0 }}>
          {ta('Filtra')}
        </button>
        {hasActiveFilters && (
          <button type="button" className="btn btn-ghost" style={{ width: 'auto', padding: '10px 16px', marginTop: 0 }} onClick={handleClearFilters}>
            {ta('Pulisci filtri')}
          </button>
        )}
      </form>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{ta('Nome')}</th>
              <th>{ta('Cognome')}</th>
              <th>{ta('Badge')}</th>
              {visibleColumns.riferimento && <th>{ta('Riferimento')}</th>}
              {visibleColumns.email && <th>{ta('Email')}</th>}
              {visibleColumns.ditta && <th>{ta('Ditta')}</th>}
              {visibleColumns.documento && <th>{ta('Documento')}</th>}
              {visibleColumns.firma && <th>{ta('Firma')}</th>}
              {visibleColumns.privacy && <th>{ta('Privacy')}</th>}
              {visibleColumns.sicurezza && <th>{ta('Sicurezza')}</th>}
              {visibleColumns.note && <th>{ta('Note')}</th>}
              {visibleColumns.dispositivo && <th>{ta('Dispositivo')}</th>}
              <th>{ta('Data')}</th>
              <th>{ta('Ora entrata')}</th>
              <th>{ta('Ora uscita')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.map((r) => (
              <tr key={r.id} style={r.has_open_duplicate ? { background: 'rgba(193, 80, 61, 0.06)' } : undefined}>
                <td>
                  <button type="button" className="link-button" onClick={() => setDetailRegistrationId(r.id)} title={ta('Vedi tutti i dati')}>
                    {r.nome}
                  </button>
                </td>
                <td>
                  <button type="button" className="link-button" onClick={() => setDetailRegistrationId(r.id)} title={ta('Vedi tutti i dati')}>
                    {r.cognome}
                  </button>
                </td>
                <td className="mono">{r.numero_badge}</td>
                {visibleColumns.riferimento && <td>{r.persona_riferimento || '—'}</td>}
                {visibleColumns.email && <td>{r.email || '—'}</td>}
                {visibleColumns.ditta && <td>{r.ditta || '—'}</td>}
                {visibleColumns.documento && (
                  <td>
                    {r.tipo_documento
                      ? `${ta(DOC_TYPE_LABELS[r.tipo_documento])}${r.numero_documento ? ' · ' + r.numero_documento : ''}`
                      : '—'}
                  </td>
                )}
                {visibleColumns.firma && (
                  <td>
                    {r.signature_data ? (
                      <button
                        type="button"
                        onClick={() => setSignaturePreview(r.signature_data)}
                        style={{ background: '#fff', border: '1px solid var(--border-hair)', borderRadius: 6, padding: 2, cursor: 'zoom-in' }}
                        title={ta('Ingrandisci firma')}
                      >
                        <img src={r.signature_data} alt={ta('Firma')} style={{ height: 22, display: 'block' }} />
                      </button>
                    ) : '—'}
                  </td>
                )}
                {visibleColumns.privacy && (
                  <td><span className={`badge-pill badge-pill--${r.privacy_accepted ? 'yes' : 'no'}`}>{r.privacy_accepted ? ta('OK') : ta('NO')}</span></td>
                )}
                {visibleColumns.sicurezza && (
                  <td><span className={`badge-pill badge-pill--${r.security_accepted ? 'yes' : 'no'}`}>{r.security_accepted ? ta('OK') : ta('NO')}</span></td>
                )}
                {visibleColumns.note && (
                  <td>
                    {r.note ? (
                      <button
                        type="button"
                        className="btn-ghost btn"
                        style={{ width: 'auto', padding: '5px 10px', fontSize: '0.82rem' }}
                        onClick={() => setNoteRegistration(r)}
                      >
                        {ta('📝 Vedi')}
                      </button>
                    ) : '—'}
                  </td>
                )}
                {visibleColumns.dispositivo && <td>{r.device_label || '—'}</td>}
                <td className="mono">{formatDatePart(r.registered_at)}</td>
                <td className="mono">{formatTimePart(r.registered_at)}</td>
                <td className="mono">
                  {r.exit_at ? formatTimePart(r.exit_at) : (
                    <span
                      className="badge-pill badge-pill--no"
                      title={r.has_open_duplicate ? ta('Questo badge ha più di una registrazione aperta: probabile uscita dimenticata') : ''}
                    >
                      {r.has_open_duplicate ? ta('⚠ In corso (duplicato)') : ta('In corso')}
                    </span>
                  )}
                </td>
                <td style={{ display: 'flex', gap: 6 }}>
                  {!r.exit_at && (
                    <button
                      className="btn-ghost btn"
                      style={{ width: 'auto', padding: '6px 10px' }}
                      onClick={() => handleClose(r)}
                      disabled={closingId === r.id}
                    >
                      {closingId === r.id ? ta('Chiusura…') : ta('Chiudi')}
                    </button>
                  )}
                  {isSuperAdmin && (
                    <button
                      className="btn-ghost btn"
                      style={{ width: 'auto', padding: '6px 10px', color: 'var(--clay-red-dark)' }}
                      onClick={() => handleDelete(r)}
                      disabled={deletingId === r.id}
                    >
                      {deletingId === r.id ? ta('Eliminazione…') : ta('Elimina')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && <p style={{ color: 'var(--muted-text)', padding: '16px 0 0' }}>{ta('Nessuna registrazione trovata.')}</p>}
        {loading && <p style={{ color: 'var(--muted-text)', padding: '16px 0 0' }}>{ta('Caricamento…')}</p>}

        <Pagination
          page={page}
          totalPages={Math.max(1, Math.ceil(total / pageSize))}
          onChange={handlePageChange}
          total={total}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      {signaturePreview && (
        <div className="modal-overlay" onClick={() => setSignaturePreview(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
            <h2>{ta('Firma')}</h2>
            <img src={signaturePreview} alt={ta('Firma ingrandita')} style={{ width: '100%', background: '#fff', border: '1px solid var(--border-hair)', borderRadius: 'var(--radius-control)' }} />
            <button type="button" className="btn btn-ghost" style={{ marginTop: 16 }} onClick={() => setSignaturePreview(null)}>{ta('Chiudi')}</button>
          </div>
        </div>
      )}

      {noteRegistration && (
        <div className="modal-overlay" onClick={() => setNoteRegistration(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2>{ta('Nota di')} {noteRegistration.nome} {noteRegistration.cognome}</h2>
            <p style={{ whiteSpace: 'pre-wrap', color: 'var(--ink-text)', fontSize: '0.92rem', lineHeight: 1.5 }}>
              {noteRegistration.note}
            </p>
            <div className="btn-row" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-primary" onClick={() => setNoteRegistration(null)}>{ta('Chiudi')}</button>
            </div>
          </div>
        </div>
      )}

      {detailRegistrationId && (
        <RegistrationDetailModal registrationId={detailRegistrationId} onClose={() => setDetailRegistrationId(null)} />
      )}
    </div>
  );
}
